import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { NavigateAfterDelete } from "@/components/NavigateAfterDelete.tsx";
import { FullPriceWithDiscount } from "@/components/PriceWithDiscount.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { Separator } from "@/components/Separator.tsx";
import { Spacer } from "@/components/Spacer.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { Toolbar } from "@/components/Toolbar.tsx";
import { WTooltip } from "@/components/WTooltip.tsx";
import { WModalButton, WTooltipButton } from "@/components/button/WToolButton.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { LongDate } from "@/components/l10n/DateFormat.tsx";
import { Gb } from "@/components/l10n/Gb.tsx";
import { ContentLoadingSuspense } from "@/components/loading/Loading.tsx";
import { DeleteModal } from "@/components/modal/DeleteModal.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableBillingAccount } from "@/components/table/DetailsTableBillingAccount.tsx";
import { MonthlyCostRow } from "@/components/table/DetailsTableMonthlyCost.tsx";
import { DetailsTableName } from "@/components/table/DetailsTableName.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { getResourceById } from "@/modules/api/resourceTypeException.tsx";
import { useStandardMutation, useStandardSuspenseQuery } from "@/modules/api/useStandardMutation.ts";
import { BillingAccountLink } from "@/modules/billing/BillingAccountLink.tsx";
import { CreateBucketModal } from "@/modules/storage/account/CreateBucketModal.tsx";
import { CreateKeyModal } from "@/modules/storage/account/CreateKeyModal.tsx";
import { SuspendedNotice } from "@/modules/storage/account/SuspendedNotice.tsx";
import { UpdateKeyModal } from "@/modules/storage/account/UpdateKeyModal.tsx";
import {
    changeStorageAccountMutation,
    deleteObjectStorageKeyMutation,
    deleteStorageAccountMutation,
    getStorageAccountKeysQuery,
} from "@/modules/storage/account/storageAccountApi.ts";
import { storageAccountListQueryWithPricesAtom } from "@/modules/storage/account/storageAccountQuery.ts";
import { type BucketWithPrice, deleteBucketMutation, getS3ApiUrlQuery } from "@/modules/storage/bucket/bucketApi.ts";
import { t_billingAccountOverrideNotice } from "@/modules/storage/bucket/bucketMessages.tsx";
import { bucketListQueryWithPricesAtom } from "@/modules/storage/bucket/bucketQuery.ts";
import { bucketLink } from "@/modules/storage/links.ts";
import { useDeletableResource } from "@/utils/query/useDeletableResource.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { BillingAccount } from "@warrenio/api-spec/spec.oats.gen";

export function StorageAccountView({ accountUuid }: { accountUuid: string }) {
    //#region Hooks
    const { t } = useLingui();
    const deleteMutation = useStandardMutation(deleteStorageAccountMutation);
    const changeMutation = useStandardMutation(changeStorageAccountMutation);

    const buckets = useSuspenseQueryAtom(bucketListQueryWithPricesAtom);

    const accountBuckets = Array.from(buckets.values()).filter((bucket) => bucket.storage_account_uuid === accountUuid);

    const data = useSuspenseQueryAtom(storageAccountListQueryWithPricesAtom);
    const obj = useDeletableResource(() => getResourceById(data, accountUuid, "storage_account"), deleteMutation);
    //#endregion

    if (obj === undefined) {
        return <NavigateAfterDelete />;
    }

    const { uuid, title, $type, billing_account_id, is_suspended, size_bytes, price, created_at, number_of_buckets } =
        obj;

    async function onDelete() {
        await deleteMutation.mutateAsync({ uuid });
    }

    async function onRename(newName: string) {
        newName = newName.trim();
        if (newName && title !== newName) {
            await changeMutation.mutateAsync({
                uuid,
                body: { title: newName },
            });
        }
    }

    async function changeBillingAccountBillingAccount(billing_account: BillingAccount) {
        await changeMutation.mutateAsync({
            uuid,
            body: { billing_account_id: billing_account.id },
        });
    }

    return (
        <>
            <Toolbar>
                <DeleteModal modalAction={async () => await onDelete()} isDisabled={!!accountBuckets.length}>
                    <Trans>
                        Deleting the storage account "{title}" will permanently remove it and delete all associated
                        keys. Accounts with active buckets cannot be deleted.
                    </Trans>
                </DeleteModal>
            </Toolbar>

            <ContentPane className="VStack gap-4">
                {is_suspended && <SuspendedNotice billing_account_id={billing_account_id} />}

                <ViewTitle title={title} />

                <DetailsHolder>
                    <DetailsTable>
                        <DetailsTableName value={title} isRequired onChange={async (title) => await onRename(title)} />

                        <DetailsTableRow title={t`Type`}>
                            <ResourceWithIcon type={$type} />
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Size`}>
                            <Gb bytes={size_bytes} />
                        </DetailsTableRow>
                        <DetailsTableRow
                            title={<Trans comment="Short of: Number of storage buckets">No. of buckets</Trans>}
                        >
                            {number_of_buckets}
                        </DetailsTableRow>
                    </DetailsTable>

                    <Spacer />

                    <DetailsTable>
                        <DetailsTableRow title={t`Server`}>
                            <S3ServerUrl />
                        </DetailsTableRow>

                        <DetailsTableRow title={t`UUID`}>
                            <ClipBoardTooltip>{uuid}</ClipBoardTooltip>
                        </DetailsTableRow>

                        {!!created_at && (
                            <DetailsTableRow title={t`Created`}>
                                <LongDate date={created_at} />
                            </DetailsTableRow>
                        )}

                        <DetailsTableRow />

                        <DetailsTableBillingAccount
                            valueKey={billing_account_id}
                            onChange={async (item) => await changeBillingAccountBillingAccount(item.account)}
                        />
                        <MonthlyCostRow price={price} billingAccountId={billing_account_id} />
                    </DetailsTable>
                </DetailsHolder>
            </ContentPane>

            <Separator />

            <ContentPane className="VStack gap-4">
                <SectionHeader title={<Trans>Storage access keys</Trans>} />
                <ContentLoadingSuspense>
                    <AccessKeysContent storageAccountUuid={uuid} isSuspended={is_suspended} />
                </ContentLoadingSuspense>
            </ContentPane>

            <Separator />

            <ContentPane className="VStack gap-4">
                <SectionHeader title={<Trans comment="List of S3 storage buckets">Storage buckets</Trans>} />
                <ContentLoadingSuspense>
                    <BucketsContent items={accountBuckets} storageAccountUuid={uuid} isSuspended={is_suspended} />
                </ContentLoadingSuspense>
            </ContentPane>
        </>
    );
}

function ViewKeyModal({ secret_key }: { secret_key: string }) {
    const { t } = useLingui();

    return (
        <WModal button={<WModalButton size="xs" label={t`View`} />}>
            <WModalContent title={t`Secret access key`} modalAction={undefined}>
                <ClipBoardTooltip>{secret_key}</ClipBoardTooltip>
            </WModalContent>
        </WModal>
    );
}

function AccessKeysContent({ storageAccountUuid, isSuspended }: { storageAccountUuid: string; isSuspended: boolean }) {
    const deleteAccessKeyMutation = useStandardMutation(deleteObjectStorageKeyMutation);

    const { data } = useStandardSuspenseQuery(getStorageAccountKeysQuery, storageAccountUuid);

    async function deleteAccessKey(access_key: string) {
        await deleteAccessKeyMutation.mutateAsync({ uuid: storageAccountUuid, access_key });
    }

    return (
        <WTable afterTable={<CreateKeyModal storageAccountUuid={storageAccountUuid} isDisabled={isSuspended} />}>
            <thead>
                <tr>
                    <th>
                        <Trans>Name</Trans>
                    </th>
                    <th>
                        <Trans>Access key ID</Trans>
                    </th>
                    <th>
                        <Trans>Secret access key</Trans>
                    </th>
                    <th>
                        <Trans>Permissions</Trans>
                    </th>
                    <th>
                        <Trans>Created at</Trans>
                    </th>
                    <th />
                </tr>
            </thead>
            <WTableBody>
                {data.map(({ access_key, secret_key, read_only, description, created_at }) => {
                    // NB: Used in translation so should not be renamed
                    const accessKey = (
                        <>
                            <b>{description}</b> {access_key}
                        </>
                    );
                    return (
                        <tr key={access_key}>
                            <td>{description}</td>
                            <td>
                                <ClipBoardTooltip>{access_key}</ClipBoardTooltip>
                            </td>
                            <td>
                                <div className="flex gap-0.5em">
                                    <ViewKeyModal secret_key={secret_key} />
                                    <ClipBoardTooltip text={secret_key}>
                                        <Trans>Copy</Trans>
                                    </ClipBoardTooltip>
                                </div>
                            </td>
                            <td>
                                {read_only ? (
                                    <Trans comment="Only read permissions">Read-only</Trans>
                                ) : (
                                    <Trans>All permissions</Trans>
                                )}
                            </td>
                            <td>{created_at && <LongDate date={created_at} />}</td>
                            <td className="text-right">
                                <div className="flex gap-0.5em justify-end">
                                    <UpdateKeyModal
                                        storageAccountUuid={storageAccountUuid}
                                        accessKey={access_key}
                                        value={description}
                                    />

                                    <DeleteModal inTable modalAction={async () => await deleteAccessKey(access_key)}>
                                        <Trans>
                                            Deleting the access key "{accessKey}" will permanently remove it, and it
                                            cannot be used for accessing the storage API after that.
                                        </Trans>
                                    </DeleteModal>
                                </div>
                            </td>
                        </tr>
                    );
                })}
            </WTableBody>
        </WTable>
    );
}

function BucketsContent({
    items,
    storageAccountUuid,
    isSuspended,
}: {
    items: BucketWithPrice[];
    storageAccountUuid: string;
    isSuspended: boolean;
}) {
    const { t } = useLingui();
    const deleteMutation = useStandardMutation(deleteBucketMutation);

    const hasBillingAccountOverrides = items.some(
        ({ billing_account_id_override }) => billing_account_id_override !== undefined,
    );

    async function onDelete(name: string) {
        await deleteMutation.mutateAsync({ name });
    }

    return (
        <WTable afterTable={<CreateBucketModal storageAccountUuid={storageAccountUuid} isDisabled={isSuspended} />}>
            <thead>
                <tr>
                    <th>
                        <Trans>Name</Trans>
                    </th>
                    <th>
                        <Trans>No. of files</Trans>
                    </th>
                    <th>
                        <Trans>Size</Trans>
                    </th>
                    <th>
                        <Trans>Created</Trans>
                    </th>
                    <th>
                        <Trans>Est. monthly cost</Trans>
                    </th>
                    {hasBillingAccountOverrides && (
                        <th>
                            <div className="flex items-center">
                                <Trans>Billing account override</Trans>
                                <WTooltip text={t_billingAccountOverrideNotice}>
                                    <MaskIcon className="ml-1 jp-warning-icon size-0.875rem text-warning" />
                                </WTooltip>
                            </div>
                        </th>
                    )}
                    <th />
                </tr>
            </thead>
            <WTableBody>
                {items.map((item) => {
                    const {
                        name,
                        num_objects,
                        size_bytes,
                        created_at,
                        price,
                        billing_account_id,
                        billing_account_id_override,
                    } = item;
                    const viewLink = bucketLink(item);

                    return (
                        <tr key={name}>
                            <td>
                                <WTooltipButton
                                    title={t`View`}
                                    color="primary"
                                    size="xs"
                                    variant="ghost"
                                    action={viewLink}
                                >
                                    {name}
                                </WTooltipButton>
                            </td>
                            <td>{num_objects}</td>
                            <td>
                                <Gb bytes={size_bytes} />
                            </td>
                            <td>
                                <LongDate date={created_at} />
                            </td>
                            <td>
                                <FullPriceWithDiscount
                                    price={price}
                                    billingAccountId={billing_account_id}
                                    showDiscount
                                    allowFree
                                />
                            </td>
                            {hasBillingAccountOverrides && (
                                <td>
                                    {billing_account_id_override !== undefined && (
                                        <BillingAccountLink billingAccountId={billing_account_id_override} />
                                    )}
                                </td>
                            )}
                            <td className="text-right">
                                <div className="flex gap-0.5em justify-end">
                                    <WTooltipButton
                                        title={t`View`}
                                        icon="jp-icon-folder"
                                        color="primary"
                                        size="xs"
                                        variant="ghost"
                                        action={viewLink}
                                    />

                                    <DeleteModal inTable modalAction={async () => await onDelete(name)}>
                                        <Trans>
                                            Deleting the storage bucket "{name}" will permanently remove it, and any
                                            files in it will not be accessible after that.
                                        </Trans>
                                    </DeleteModal>
                                </div>
                            </td>
                        </tr>
                    );
                })}
            </WTableBody>
        </WTable>
    );
}

export function S3ServerUrl() {
    const s3Url = useStandardSuspenseQuery(getS3ApiUrlQuery);

    const s3Server = new URL(s3Url.data.url).host;

    return <ClipBoardTooltip>{s3Server}</ClipBoardTooltip>;
}
